// Shared date-bucketing helpers — used by both `time-bucket` (verb)
// and `date-bucket` / `to-date` (scalar). SSOT for the period enum,
// the dayjs `startOf` unit mapping, and the dayjs plugin
// installation. The kernel parses window bounds, buckets, and
// formats in the app's display timezone (`env.displayTz`) — the one
// zone the LLM is given, the user reasons in, and `formatDateValue`
// already renders — so a date the user
// asks for and the bucket label they see agree end-to-end. Datapoint
// timestamps stay absolute instants (`toDateValue`); only bucket
// boundaries + display strings are zone-relative. Importing this
// module installs the dayjs plugins as a side-effect.

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

import { DATE_FORMAT_PATTERN, ISO_DATE_REGEX_BASIC } from '#core/lib/utils/date.ts';
import { env } from '#core/lib/utils/env.ts';
import { trieMemoize } from '#core/lib/utils/map-trie.ts';
import { instanceKey, isInstanceReference } from '#core/schema/utils.ts';

import { NO_POS } from '../../ast.ts';
import { DirectRelationScalarComparisonError } from '../../errors.ts';
import { cellNumber, numericScalarOf } from '../../value.ts';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(quarterOfYear);

/** Period domain for `time-bucket` and `date-bucket`. */
export const Periods = ['minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'] as const;
export type Period = (typeof Periods)[number];

/**
 * Period → dayjs `startOf` unit lookup. Same enum values, exposed
 * as a typed `Record` so callers can index without an `as` cast.
 */
export const PERIOD_TO_DAYJS: Record<Period, dayjs.OpUnitType | dayjs.QUnitType> = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
};
/**
 * Argument normalizer for `Map`-keyed caches and tries (including
 * {@link trieMemoize}): collapse `Date` instances to their epoch-ms so
 * logically-equal instants share one cache cell. Distinct `Date`
 * objects with the same `getTime()` are reference-distinct under
 * `Map`'s SameValueZero — without this they'd split into separate
 * buckets. Every other value passes through unchanged.
 *
 * SSOT for the Date-aware Map-key normalization pattern, shared by the
 * date-bucket memo callers in this file and the row/window partitioners
 * in `runtime/arquero.ts`.
 */
export const dateKey = (v: unknown): unknown => (v instanceof Date ? v.getTime() : v);

/**
 * Parse an `unknown` value to a `Date` in UTC mode, or `null` if
 * the value is null / invalid. ISO-8601 strings, epoch-ms numbers,
 * and `Date` values are accepted. SSOT for the "any input → Date"
 * coercion used by `to-date` and `bucketTimestamp`.
 */
export const toDateValue = (v: unknown): Date | null => {
  if (v === null || v === undefined) {
    return null;
  }
  if (v instanceof Date) {
    return v;
  }
  const d = dayjs.utc(v as never);
  return d.isValid() ? d.toDate() : null;
};
/**
 * Date-aware numeric coercion for the chain comparison ops
 * (`<`, `<=`, `>`, `>=`). `Number`-first (via the box-aware
 * `cellNumber`, so a unit-bearing cell compares as its bare number);
 * numbers, numeric strings, `Date` (→ epoch-ms), booleans and `null`
 * keep their exact prior semantics; only when the coercion would be
 * `NaN` do we rescue ISO-8601 / offset date strings via `toDateValue`
 * so a `timestamp` column compares correctly against a string-literal
 * date bound. Genuinely non-numeric, non-date input still yields
 * `NaN` (comparison ⇒ false).
 * SSOT for "compare a value as a number, tolerating dates".
 */
export const toComparableNumber = (v: unknown): number => {
  const n = cellNumber(v);
  if (!Number.isNaN(n)) {
    return n;
  }
  const d = toDateValue(v);
  return d ? d.getTime() : Number.NaN;
};
const isDateLike = (v: unknown): boolean =>
  v instanceof Date || (typeof v === 'string' && ISO_DATE_REGEX_BASIC.test(v));

/**
 * Universal guard for the `=` / `!=` / `in` comparison SSOT. An instance
 * reference (a direct-relation `{space, externalId}` struct) compared to a
 * scalar can never match, and silently filtering to zero rows hides a real
 * query mistake (e.g. `(in (:wellString %) (col ent externalId))` — a
 * struct column against a bare `externalId` set). When exactly one side is
 * an instance reference and the other a non-null scalar, throw with the two
 * recovery shapes instead. A `null` / `undefined` counterpart is left alone
 * (legitimate null / `is-null` semantics). Same-type pairs — struct⋈struct
 * or scalar⋈scalar — pass through untouched.
 */
const assertComparableCells = (a: unknown, b: unknown): void => {
  const aRef = isInstanceReference(a);
  const bRef = isInstanceReference(b);
  if (aRef === bRef) {
    return;
  }
  const counterpart = aRef ? b : a;
  if (counterpart === null || counterpart === undefined) {
    return;
  }
  // JSON-render so the message stays legible: strings get quoted, and an
  // unexpected non-ref struct shows its shape instead of `[object Object]`.
  throw new DirectRelationScalarComparisonError(JSON.stringify(counterpart), NO_POS);
};
/**
 * Asymmetric date-aware equality for `=` / `!=` / `in`. Strict
 * `===` first, then a string-vs-string case-insensitive bridge
 * (this is an LLM-facing tool — casing must be forgiving, so
 * `(= status "ACTIVE")` matches an `"active"` cell). Cross-type
 * strictness is untouched: the case-insensitive path is gated on
 * BOTH sides being strings, so `(= 1 "1")` / `(= isActive "true")`
 * stay `false` per the documented strict-equality invariant —
 * number/boolean semantics are unchanged. Only when at least one
 * side is genuinely date-shaped (a `Date`, or an ISO-`YYYY-MM-DD…`
 * string) do we bridge the gap by comparing parsed instants — so
 * `(= day "2025-10-01")` against a `timestamp` column (a `Date`
 * cell) does the obvious right thing instead of silently never
 * matching. SSOT for "are these two values the same instant".
 *
 * NOTE: case-insensitivity is local-eval only. CDF push-down maps
 * `=`/`in`/`prefix` to server-side filter leaves that match
 * case-sensitively (CDF exposes no case-insensitive option), so a
 * pushed-down query can match differently than the same query run
 * in-memory. This divergence is accepted by design.
 *
 * Rejects an instance-reference-vs-scalar comparison up front (see
 * `assertComparableCells`) so a direct-relation column filtered against a
 * bare `externalId` set errors with recovery guidance instead of silently
 * matching nothing. Two instance references compare by STRUCTURAL identity
 * (`instanceKey` — the same SSOT array membership uses): every row
 * materialises its own `{space, externalId}` cell object, so `===` would
 * never match and a relation⋈relation equality would silently be false.
 */
export const dateAwareEquals = (rawA: unknown, rawB: unknown): boolean => {
  // Unit-bearing cells compare as their bare numbers — boxed 5 equals
  // plain 5; units never affect equality.
  const a = numericScalarOf(rawA) ?? rawA;
  const b = numericScalarOf(rawB) ?? rawB;
  assertComparableCells(a, b);
  if (a === b) {
    return true;
  }
  if (isInstanceReference(a) && isInstanceReference(b)) {
    return instanceKey(a) === instanceKey(b);
  }
  // String-vs-string only ever short-circuits to `true` — never
  // `false` — so two textually-different but same-instant ISO
  // strings still reach the date bridge below.
  if (typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase()) {
    return true;
  }
  if (!isDateLike(a) && !isDateLike(b)) {
    return false;
  }
  const da = toDateValue(a);
  const db = toDateValue(b);
  return da !== null && db !== null && da.getTime() === db.getTime();
};

/**
 * Bucket a value to the start of `period` in the display timezone
 * (`env.displayTz`). The input is an absolute instant (datapoint
 * timestamp / window value); the bucket boundary is the start of the
 * `env.displayTz` period that instant falls in, returned as an
 * absolute `Date`. Returns `null` for invalid input. SSOT for the
 * bucketing kernel shared by `time-bucket` and `date-bucket`.
 */
export const computeBucketTimestamp = (v: unknown, period: Period): Date | null => {
  const d = toDateValue(v);
  if (d === null) {
    return null;
  }
  return dayjs(d).tz(env.displayTz).startOf(PERIOD_TO_DAYJS[period]).toDate();
};

const bucketTimestampMemo = trieMemoize(
  (_tz: string, v: unknown, period: Period) => computeBucketTimestamp(v, period),
  dateKey,
);
export const bucketTimestamp = (v: unknown, period: Period): Date | null =>
  bucketTimestampMemo(env.displayTz, v, period);

/**
 * Format a value (ISO string / epoch-ms / `Date`) as a string in the
 * display timezone (`env.displayTz`) using a dayjs pattern (default
 * `YYYY-MM-DD`). Returns `null` for null / invalid input. SSOT for
 * the Date → display-string direction — the inverse of `toDateValue`.
 * Renders in `env.displayTz` (same zone `formatDate`/`formatDateTime`
 * and the bucketing kernel use) so a bucket label matches the date
 * the user asked for.
 */
export const formatDateValue = (v: unknown, pattern = DATE_FORMAT_PATTERN): string | null => {
  const d = toDateValue(v);
  return d === null ? null : dayjs(d).tz(env.displayTz).format(pattern);
};
/**
 * Does this value carry a wall-clock time-of-day in the display timezone?
 *
 * Bare date strings (no ISO `'T'`) are date-only by definition. For `Date`
 * and epoch-ms values the instant is projected into {@link env.displayTz}
 * and is considered date-only iff it lands exactly on midnight there — so a
 * `…T00:00:00+04:00` instant reads as a pure date even though its UTC
 * wall-clock is 20:00. SSOT for the "show the time or not" decision.
 */
export const hasTimeComponent = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.includes('T');
  }
  const d = toDateValue(value);
  if (d === null) {
    return false;
  }
  const z = dayjs(d).tz(env.displayTz);
  return z.hour() !== 0 || z.minute() !== 0 || z.second() !== 0 || z.millisecond() !== 0;
};

/**
 * Canonical human display format for a timestamp: render in
 * {@link env.displayTz}, dropping the time component when the instant is a
 * pure date (midnight in the display zone — see {@link hasTimeComponent}).
 * `date`/`dateTime` default to `YYYY-MM-DD` / `YYYY-MM-DD HH:mm`, but each
 * call site may pass its own pattern pair. Returns `null` for invalid input.
 * Pattern-exact rendering (expr ops, pushdown normalisation) must keep using
 * {@link formatDateValue} directly so midnight-drop never leaks into
 * machine-facing output.
 */
export const formatDisplayInstant = (
  value: unknown,
  patterns: { date?: string; dateTime?: string } = {},
): string | null => {
  const { date = DATE_FORMAT_PATTERN, dateTime = 'YYYY-MM-DD HH:mm' } = patterns;
  return formatDateValue(value, hasTimeComponent(value) ? dateTime : date);
};

// An ISO-8601 datetime is *absolute* iff it carries a zone designator
// (`Z` or `±HH[:]MM`); without one it is a *local civil* datetime
// (ISO-8601 §4.3 / RFC-3339 "local"). This classifies which — it
// never mutates the string. (`dayjs.tz(str, zone)` cannot be used
// unconditionally: it misreads an embedded offset, e.g.
// `…+04:00` → wrong instant — verified — so the absolute case must
// parse the offset directly.)
const ISO_HAS_ZONE = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

/**
 * Parse a `:window` bound to the absolute instant it denotes.
 *
 * Window bounds are user/LLM-authored calendar intent: the model is
 * told the current date in `env.displayTz`, so a bare `"2026-01-01"`
 * (or any offset-less / date-only ISO string) means "that date *in
 * the display zone*" — parsed as civil time in `env.displayTz`. A
 * string that DOES carry an explicit zone (`Z` / `+04:00`), or a
 * non-string `Date`/epoch-ms, is already an unambiguous instant and
 * is honoured exactly (then bucketed onto the display-zone grid).
 * Returns `null` on an invalid bound (mirrors `periodGrid`'s `[]`).
 * NOT used for datapoint timestamps — those are absolute instants
 * (`toDateValue`).
 *
 * Memoised (keyed by the bound + `env.displayTz`): a per-cell
 * `time-bucket` lift re-parses the SAME two window bounds for every
 * lifted cell — this was a slice of the profiled freeze. Pure
 * function of `(bound, env.displayTz)`, so the cache is exact.
 */
const computeParseWindowBound = (bound: unknown): Date | null => {
  if (bound === null || bound === undefined) {
    return null;
  }
  // Absolute: zoned ISO string, or a Date / epoch-ms — honour as-is.
  if (typeof bound !== 'string' || ISO_HAS_ZONE.test(bound)) {
    const abs = dayjs(bound as never);
    return abs.isValid() ? abs.toDate() : null;
  }
  // Local civil time: a bare date / offset-less datetime is calendar
  // intent in the app display zone (the zone the LLM is given).
  // `dayjs.tz` THROWS on an unparseable bound (it does not return an
  // invalid instance) — guard so the contract stays "invalid → null".
  try {
    const civil = dayjs.tz(bound, env.displayTz);
    return civil.isValid() ? civil.toDate() : null;
  } catch {
    return null;
  }
};

const parseWindowBoundMemo = trieMemoize((_tz: string, bound: unknown) => computeParseWindowBound(bound), dateKey);
export const parseWindowBound: (bound: unknown) => Date | null = (bound) => parseWindowBoundMemo(env.displayTz, bound);

/**
 * Enumerate every period-start key in the half-open window
 * `[start, end)` at `period` granularity, in the display timezone
 * (`env.displayTz`), ascending. SSOT for the deterministic `:window`
 * grid `time-bucket`'s per-cell form emits: every lifted TS-ref cell
 * — across every axis — produces a row for every key here (gap
 * buckets null-filled), so the lifted columns share a byte-identical
 * bucket set. That is the positional-zip precondition multi-column
 * `unnest` relies on. `start`/
 * `end` are parsed via `parseWindowBound` (display-zone calendar
 * intent); keys are display-zone period starts as absolute `Date`s.
 * An invalid bound or `end <= start` yields `[]`.
 *
 * Memoised (keyed by `start`/`end`/`period`/`env.displayTz`): the
 * per-cell `time-bucket` lift invokes this once PER lifted cell with
 * identical call-level arguments — recomputing the same dayjs-tz grid
 * hundreds of times was ~60% of the profiled freeze. Pure function of
 * its key, so the cache is exact. The returned array is shared and
 * MUST be treated read-only by callers (they only `.map`/iterate it).
 */
const computePeriodGrid = (start: unknown, end: unknown, period: Period): readonly Date[] => {
  const s = parseWindowBound(start);
  const e = parseWindowBound(end);
  if (s === null || e === null) {
    return [];
  }
  const unit = PERIOD_TO_DAYJS[period];
  const stop = dayjs(e).tz(env.displayTz);
  const out: Date[] = [];
  let cursor = dayjs(s).tz(env.displayTz).startOf(unit);
  while (cursor.isBefore(stop)) {
    out.push(cursor.toDate());
    cursor = cursor.add(1, unit as dayjs.ManipulateType);
  }
  return out;
};

const periodGridMemo = trieMemoize(
  (_tz: string, period: Period, start: unknown, end: unknown) => computePeriodGrid(start, end, period),
  dateKey,
);
export const periodGrid: (start: unknown, end: unknown, period: Period) => readonly Date[] = (start, end, period) =>
  periodGridMemo(env.displayTz, period, start, end);

/**
 * Display-timezone period-aligned half-open `[start, end)` epoch-ms
 * bounds that enclose the `[start, end]` window at `period`
 * granularity — the SAME alignment `periodGrid` / `bucketTimestamp`
 * use (`env.displayTz`). SSOT for any consumer that must fetch or
 * aggregate on the exact grid the engine buckets on (e.g. the chat
 * TS-ref fetcher widening a CDF datapoints range so the first/last
 * grid buckets have complete underlying data). `start` snaps DOWN
 * (floor) to the enclosing period start; `end` snaps UP (ceil) to the
 * smallest period boundary `>= end` — so a window whose end is
 * already on a period boundary is NOT over-expanded by a spurious
 * trailing period, exactly matching `periodGrid`'s half-open
 * `[start, end)` last-key rule. Bounds are parsed via
 * `parseWindowBound` (display-zone calendar intent); the returned
 * epoch-ms are absolute instants (correct for a CDF query). Returns
 * `null` on an invalid bound (mirrors `periodGrid`'s `[]`).
 *
 * Memoised on the same key shape as `periodGrid` (it does the same
 * dayjs-tz work and is called once per fetch with call-level args).
 */
const computePeriodAlignedBoundsMs = (
  start: unknown,
  end: unknown,
  period: Period,
): { readonly start: number; readonly end: number } | null => {
  const s = parseWindowBound(start);
  const e = parseWindowBound(end);
  if (s === null || e === null) {
    return null;
  }
  const unit = PERIOD_TO_DAYJS[period];
  const endTz = dayjs(e).tz(env.displayTz);
  const endFloor = endTz.startOf(unit);
  return {
    start: dayjs(s).tz(env.displayTz).startOf(unit).valueOf(),
    end: endFloor.isSame(endTz) ? endFloor.valueOf() : endFloor.add(1, unit as dayjs.ManipulateType).valueOf(),
  };
};
const periodAlignedBoundsMsMemo = trieMemoize(
  (_tz: string, period: Period, start: unknown, end: unknown) => computePeriodAlignedBoundsMs(start, end, period),
  dateKey,
);
export const periodAlignedBoundsMs: (
  start: unknown,
  end: unknown,
  period: Period,
) => { readonly start: number; readonly end: number } | null = (start, end, period) =>
  periodAlignedBoundsMsMemo(env.displayTz, period, start, end);
